import React from "react";
import styled from "styled-components";
import { spacing } from "../../../design-system/tokens/spacing";
import { typographyMixin } from "../../../design-system/tokens/typography";
import { rawColors } from "../../../design-system/tokens/colors";
import { borderRadius } from "../../../design-system/tokens/borders";
import { SchemaListItem } from "../../atoms/SchemaListItem";

export interface SchemaListItemData {
  name: string;
  /** Selected-table count, drives the per-item badge. */
  selectedCount?: number;
}

export interface SchemaListProps {
  items: SchemaListItemData[];
  activeName?: string | null;
  /** Total selected count across all schemas. Drives the "N selected" header pill. */
  totalSelected?: number;
  onSelect?: (name: string) => void;
  className?: string;
}

const Card = styled.aside`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
  width: 200px;
  flex: 0 0 auto;
  padding: ${spacing[3]};
  background-color: ${rawColors.structural.white};
  border: 1px solid ${rawColors.stroke[200]};
  border-radius: ${borderRadius.lg};
  align-self: flex-start;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 ${spacing[2]};
`;

const HeaderTitle = styled.span`
  ${typographyMixin("body", 3)}
  color: ${rawColors.text.highEmphasis};
`;

const HeaderBadge = styled.span`
  ${typographyMixin("caption", 3)}
  color: ${rawColors.primary[500]};
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[1]};
`;

/**
 * SchemaList — sidebar card listing the connector's schemas. Stateless: the
 * caller owns active-schema and selection counts. Composed from `SchemaListItem`.
 */
export const SchemaList: React.FC<SchemaListProps> = ({
  items,
  activeName,
  totalSelected = 0,
  onSelect,
  className,
}) => (
  <Card className={className}>
    <Header>
      <HeaderTitle>Schemas</HeaderTitle>
      {totalSelected > 0 ? (
        <HeaderBadge>{totalSelected} selected</HeaderBadge>
      ) : null}
    </Header>
    <List>
      {items.map((item) => (
        <SchemaListItem
          key={item.name}
          name={item.name}
          count={item.selectedCount}
          active={item.name === activeName}
          onClick={() => onSelect?.(item.name)}
        />
      ))}
    </List>
  </Card>
);

export default SchemaList;
